import { useMutation, useQueryClient } from "react-query";
import { IObjectDefaultsModel, ISaveObjectDefaultsModel } from "../../services/defaults-data-service/Models";
import DefaultsDataService from "../../services/defaults-data-service/DefaultsDataService";
import { BaseUseQueryParameters } from "../BaseUseQueryParameters";
import { MutationContextType } from "./Common";
import { keys } from "./Keys";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { RestError } from "../../services/rest-service/RestService";

export type UseSaveObjectDefaultsQueryParams = {
    groupID: number,
    eLynxObject: IeLynxObject
} & BaseUseQueryParameters<void>;

export function useSaveObjectDefaults(params: UseSaveObjectDefaultsQueryParams) {
    const queryClient = useQueryClient();

    return useMutation<void, RestError, IObjectDefaultsModel, MutationContextType>(
        (defaults) => {
            const saveModel = {
                defaults: defaults.defaults
            } as ISaveObjectDefaultsModel;
            return DefaultsDataService.saveDefaults(params.groupID, params.eLynxObject, saveModel)
        },
        {
            onMutate: async (defaults: IObjectDefaultsModel) => {
                await queryClient.cancelQueries(keys.all);

                const previousDefaults = queryClient.getQueryData<IObjectDefaultsModel | undefined>(
                    keys.defaults(params.groupID, params.eLynxObject)
                );

                queryClient.setQueryData<IObjectDefaultsModel>(
                    keys.defaults(params.groupID, params.eLynxObject),
                    (old?: IObjectDefaultsModel) => {
                        return old ?? defaults;
                    }
                );

                return {
                    previousDefaults: previousDefaults
                }
            },
            onError: params.onError,
            onSuccess: params.onSuccess,
            onSettled: (_data, error, _defaults, context) => {
                if (error && context?.previousDefaults) {
                    queryClient.setQueryData<IObjectDefaultsModel>(
                        keys.defaults(params.groupID, params.eLynxObject),
                        context?.previousDefaults
                    );
                }

                queryClient.invalidateQueries(keys.all);
            }
        }
    )
}